'use client';

import { useEffect, useRef } from 'react';

import { useTenantCurrency } from '@kit/ui/currency-icon';

import { useOptionalTenantAnalytics } from './tenant-analytics-provider';
import { isTenantAnalyticsDeliveryComplete } from './tenant-delivery-status';

const DEDUP_STORAGE_PREFIX = 'tenant-analytics-begin-checkout';

/**
 * Fires one canonical begin_checkout when a payable checkout surface renders.
 * Deduplicated per browser tab session (sessionStorage) so refreshes of an
 * unpaid payment page do not inflate the mid-funnel numbers, while a genuine
 * later return to checkout in a new tab/session counts again. Never throws —
 * uses the nullable analytics context because payment pages must not crash
 * over analytics.
 */
export function TenantBeginCheckout({
  contentId,
  contentName,
  contentType,
  value,
}: {
  readonly contentId: string;
  readonly contentType: string;
  readonly contentName?: string;
  readonly value?: number;
}) {
  const currency = useTenantCurrency().trim().toUpperCase();
  const analytics = useOptionalTenantAnalytics();
  const attemptedKeyRef = useRef<string | null>(null);
  const enabled = analytics?.enabled ?? false;
  const transportKey = analytics?.transportKey ?? null;
  const trackBeginCheckout = analytics?.trackBeginCheckout;

  useEffect(() => {
    if (!enabled || !transportKey || !trackBeginCheckout) {
      return;
    }

    const dedupKey = [
      DEDUP_STORAGE_PREFIX,
      transportKey,
      contentType,
      contentId,
    ].join(':');

    if (attemptedKeyRef.current === dedupKey || readMarker(dedupKey)) {
      return;
    }

    attemptedKeyRef.current = dedupKey;

    const content = {
      contentId,
      contentType,
      ...(contentName ? { contentName } : {}),
    };

    void (
      value !== undefined
        ? trackBeginCheckout({ ...content, value, currency })
        : trackBeginCheckout(content)
    ).then(
      (results) => {
        if (isTenantAnalyticsDeliveryComplete(results)) {
          writeMarker(dedupKey);
          return;
        }

        attemptedKeyRef.current = null;
      },
      () => {
        attemptedKeyRef.current = null;
      },
    );
  }, [
    contentId,
    contentName,
    contentType,
    currency,
    enabled,
    trackBeginCheckout,
    transportKey,
    value,
  ]);

  return null;
}

function readMarker(key: string) {
  try {
    return window.sessionStorage.getItem(key) !== null;
  } catch {
    return false;
  }
}

function writeMarker(key: string) {
  try {
    window.sessionStorage.setItem(key, '1');
  } catch {
    // Storage can be unavailable in privacy modes; the in-memory ref still
    // guards this mount.
  }
}
